import React from "react";

const altInputCls =
  "w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none";

/**
 * Upload-only catalog photos: structured list with Remove (S3 delete queued until parent Save).
 */
export default function InventoryProductPhotosSection({
  photos,
  onRemove,
  onChangePhotoAlt,
  onFileInputChange,
  uploading,
  helpText,
}) {
  return (
    <div className="space-y-4">
      {helpText ? <div className="text-xs text-gray-500 space-y-1">{helpText}</div> : null}

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="file"
          accept="image/*"
          multiple
          disabled={uploading}
          onChange={onFileInputChange}
          className="block w-full max-w-xl text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-blue-600 file:text-white hover:file:bg-blue-700"
        />
        {uploading ? <span className="text-sm text-gray-600">Uploading…</span> : null}
      </div>

      {photos.length === 0 ? (
        <p className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg px-4 py-6 text-center bg-gray-50">
          No photos yet. Choose image files above — each successful upload appears here. Removals take effect when you
          save the form (S3 objects are deleted then).
        </p>
      ) : (
        <ul className="space-y-3">
          {photos.map((p) => (
            <li
              key={p.clientId}
              className="flex flex-wrap gap-3 items-start rounded-lg border border-gray-200 bg-gray-50/80 p-3"
            >
              <img
                src={p.url}
                alt={p.alt || "Product"}
                className="h-20 w-20 shrink-0 rounded-md object-cover border border-gray-200 bg-white"
                onError={(e) => {
                  e.currentTarget.style.opacity = "0.35";
                }}
              />
              <div className="min-w-0 flex-1 space-y-2">
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-0.5">Image URL</p>
                  <code className="text-xs break-all text-gray-900 block leading-relaxed">{p.url}</code>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-0.5" htmlFor={`photo-alt-${p.clientId}`}>
                    Alt text (accessibility)
                  </label>
                  <input
                    id={`photo-alt-${p.clientId}`}
                    type="text"
                    className={altInputCls}
                    maxLength={240}
                    value={p.alt != null ? p.alt : ""}
                    onChange={(e) => onChangePhotoAlt && onChangePhotoAlt(p.clientId, e.target.value)}
                    disabled={!onChangePhotoAlt}
                    placeholder="Short description for screen readers and SEO"
                  />
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Shown on the public catalog. If you leave this blank, the save action still stores a default from the
                    product name.
                  </p>
                </div>
                {p.s3Key ? (
                  <p className="text-[11px] text-gray-500 break-all">
                    S3 key <code className="bg-white px-1 rounded">{p.s3Key}</code>
                  </p>
                ) : (
                  <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1 inline-block">
                    Legacy image (no S3 key on file). Remove drops it from this product only — not deleted from external
                    hosting.
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => onRemove(p.clientId)}
                className="shrink-0 text-sm text-red-700 hover:text-red-900 font-medium px-2 py-1 rounded border border-red-200 hover:bg-red-50"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
